// The accent system for the care-plan render primitives, mapped onto the
// brand hue tokens in libs/frontend/atoms/src/theme.css. Pick an accent per
// domain concept (a shape, a phase position) and apply its role classes.
//
// Classes are spelled out literally (not template-built) so Tailwind's static
// scanner sees them.

export type AccentName =
  | 'orange'
  | 'yellow'
  | 'lightgreen'
  | 'positive'
  | 'teal';

export type Accent = {
  name: AccentName;
  /** Accent-colored text: eyebrows, icons, inline labels. */
  text: string;
  /** Tinted tile/chip surface with accent text (IconTile, MonoChip, badges). */
  tile: string;
  /** Left accent edge on a Panel (includes the width). */
  edgeL: string;
  /** Top accent edge on a Panel (includes the width). */
  edgeT: string;
  /** Solid accent surface: section-header bars, dots, band fills. */
  fill: string;
};

export const ACCENTS: Record<AccentName, Accent> = {
  orange: {
    name: 'orange',
    text: 'text-text-orange-subtle',
    tile: 'bg-bg-orange-subtle text-text-orange-subtle',
    edgeL: 'border-l-4 border-l-bg-orange-strong',
    edgeT: 'border-t-4 border-t-bg-orange-strong',
    fill: 'bg-bg-orange-strong',
  },
  yellow: {
    name: 'yellow',
    text: 'text-text-yellow-subtle',
    tile: 'bg-bg-yellow-subtle text-text-yellow-subtle',
    edgeL: 'border-l-4 border-l-bg-yellow-strong',
    edgeT: 'border-t-4 border-t-bg-yellow-strong',
    fill: 'bg-bg-yellow-strong',
  },
  lightgreen: {
    name: 'lightgreen',
    text: 'text-text-lightgreen-subtle',
    tile: 'bg-bg-lightgreen-subtle text-text-lightgreen-subtle',
    edgeL: 'border-l-4 border-l-bg-lightgreen-strong',
    edgeT: 'border-t-4 border-t-bg-lightgreen-strong',
    fill: 'bg-bg-lightgreen-strong',
  },
  positive: {
    name: 'positive',
    text: 'text-text-positive-subtle',
    tile: 'bg-bg-positive-subtle text-text-positive-subtle',
    edgeL: 'border-l-4 border-l-bg-positive-strong-primary',
    edgeT: 'border-t-4 border-t-bg-positive-strong-primary',
    fill: 'bg-bg-positive-strong-primary',
  },
  teal: {
    name: 'teal',
    text: 'text-text-teal-subtle',
    tile: 'bg-bg-teal-subtle text-text-teal-subtle',
    edgeL: 'border-l-4 border-l-bg-teal-strong',
    edgeT: 'border-t-4 border-t-bg-teal-strong',
    fill: 'bg-bg-teal-strong',
  },
};

// The progression scale for ordered sequences (e.g. phases): warm start → cool
// end-state. `scaleAccent` distributes any count across it.
export const ACCENT_SCALE: AccentName[] = [
  'orange',
  'yellow',
  'lightgreen',
  'positive',
  'teal',
];

export function scaleAccent(index: number, count: number): Accent {
  const last = ACCENT_SCALE.length - 1;
  const position =
    count <= 1
      ? last
      : Math.round((Math.min(index, count - 1) * last) / (count - 1));
  return ACCENTS[ACCENT_SCALE[position] as AccentName];
}
